/* eslint-disable @typescript-eslint/no-explicit-any */
import { injectable } from "tsyringe";
import { ThemeMode } from "./DomThemes";
import log from "loglevel";
import { ProjectLocalEvent, ProjectMetadata, ProjectProjectionResult } from "@/ProjectWorld";
import { EventBus } from "@utilitydelta/clientside-event-sourcing";

export const ServerIssueType = {
    NotFound: 0,
    NoPermission: 1,
    ServerError: 3,
    PendingDecryption: 4,
    Resolved: 5,
} as const;

export type ServerIssueType = typeof ServerIssueType[keyof typeof ServerIssueType];

export type ProjectBusEvents = {
    PROJECT_METADATA_UPDATED: { projectMetadata: ProjectMetadata; };

    EVENT_RAISED: { projectEvent: ProjectLocalEvent; };

    SERVER_ISSUE: { serverIssue: ServerIssueType; };

    PROJECTION_UPDATED: {
        projectId: string;
        mutationResult: ProjectProjectionResult;
    }

    NAVIGATION_CHANGED: { isBackButton: boolean; };

    THEME_CHANGED: {
        theme: ThemeMode;
        isDarkMode: boolean;
    };

    KEY_DOWN: {
        key: string;
        code: string;
        altKey: boolean;
        ctrlKey: boolean;
        metaKey: boolean;
        shiftKey: boolean;
        preventDefault: () => void;
        stopPropagation: () => void;
    }

    CONNECTIVITY_CHANGED: {
        isOnline: boolean;
    };

    DIALOG_STATE_CHANGED: {
        isOpen: boolean;
        dialogTitle: string;
        dialogDescription?: string;
    }

    TAB_FOCUS_CHANGED: { isFocused: boolean };

    PROJECT_LOADED: {
        projectId: string;
    }

    OTHER_WIND0W_LOCAL_EVENTS: {
        projectId: string
        projectEvents: ProjectLocalEvent[]
    }

    AUTHENTICATION_CHANGED: { isAuthenticated: boolean; user?: any; error?: string };
};

export const TOKEN_IProjectBus = Symbol('IProjectBus');

export interface IProjectBus {


    authenticationChanged(data: { isAuthenticated: boolean; user?: any; error?: string }): void;
    subscribe_authenticationChanged(handler: (data: ProjectBusEvents['AUTHENTICATION_CHANGED']) => Promise<void>): { unsubscribe: () => void };

    serverIssue(serverIssue: ServerIssueType): void;
    subscribe_serverIssue(handler: (data: ProjectBusEvents['SERVER_ISSUE']) => Promise<void>): { unsubscribe: () => void };

    dialogStateChanged(isOpen: boolean, dialogTitle: string, dialogDescription?: string): void;
    subscribe_dialogStateChanged(handler: (data: ProjectBusEvents['DIALOG_STATE_CHANGED']) => Promise<void>): { unsubscribe: () => void };
    projectMetadataUpdated(projectMetadata: ProjectMetadata): void;
    subscribe_projectMetadataUpdated(handler: (data: ProjectBusEvents['PROJECT_METADATA_UPDATED']) => Promise<void>): { unsubscribe: () => void };
    // when a key is pressed - except for inputs / editable fields
    keyDown(key: string,
        code: string,
        altKey: boolean,
        ctrlKey: boolean,
        metaKey: boolean,
        shiftKey: boolean,
        preventDefault: () => void,
        stopPropagation: () => void): void;
    subscribe_keyDown(handler: (data: ProjectBusEvents['KEY_DOWN']) => Promise<void>): { unsubscribe: () => void };

    // This event occurs when a user generates a new local event,
    // either from undo/redo, or some user action.
    // It's used to trigger a persistence call to the server to update the server db
    eventRaised(projectEvent: ProjectLocalEvent): void;
    subscribe_eventRaised(handler: (data: ProjectBusEvents['EVENT_RAISED']) => Promise<void>): { unsubscribe: () => void };

    // This event is triggered when we receive new events - either from the server,
    // when we do an undo or redo, or when the user generates a local event. We don't
    // want to rebuild the entire MobX state for these events, we just interpret them 
    // and apply the change to the state 
    projectionUpdated(projectId: string, mutationResult: ProjectProjectionResult): void;
    subscribe_projectionUpdated(handler: (data: ProjectBusEvents['PROJECTION_UPDATED']) => Promise<void>): { unsubscribe: () => void };

    // This is important as it allows our MobX state to update based on the current URL
    // That in turn controls what JSX components we render
    userHitBackButton(): void;
    subscribe_userHitBackButton(handler: (data: ProjectBusEvents['NAVIGATION_CHANGED']) => Promise<void>): { unsubscribe: () => void };

    // This event is triggered when the theme changes, either manually or due to system preference changes
    themeChanged(theme: ThemeMode, isDarkMode: boolean): void;
    subscribe_themeChanged(handler: (data: ProjectBusEvents['THEME_CHANGED']) => Promise<void>): { unsubscribe: () => void };

    // This event is triggered when connectivity status changes (online/offline)
    connectivityChanged(isOnline: boolean): void;
    subscribe_connectivityChanged(handler: (data: ProjectBusEvents['CONNECTIVITY_CHANGED']) => Promise<void>): { unsubscribe: () => void };

    projectLoaded(projectId: string): void;
    subscribe_projectLoaded(handler: (data: ProjectBusEvents['PROJECT_LOADED']) => Promise<void>): { unsubscribe: () => void };

    // Event for tab focus change
    tabFocusChanged(isFocused: boolean): void;
    subscribe_tabFocusChanged(handler: (data: ProjectBusEvents['TAB_FOCUS_CHANGED']) => Promise<void>): { unsubscribe: () => void };

    otherWindowLocalEvents(projectId: string, events: ProjectLocalEvent[]): void;
    subscribe_otherWindowLocalEvents(handler: (data: ProjectBusEvents['OTHER_WIND0W_LOCAL_EVENTS']) => Promise<void>): { unsubscribe: () => void };
}

@injectable()
export class ProjectBus implements IProjectBus {
    private eventBus: EventBus<ProjectBusEvents>;

    constructor() {
        this.eventBus = new EventBus<ProjectBusEvents>();
    }

    public authenticationChanged(data: { isAuthenticated: boolean; user?: any; error?: string }): void {
        this.eventBus.publish('AUTHENTICATION_CHANGED', data);
    }

    subscribe_authenticationChanged(handler: (data: ProjectBusEvents['AUTHENTICATION_CHANGED']) => Promise<void>): { unsubscribe: () => void } {
        return this.eventBus.subscribe('AUTHENTICATION_CHANGED', handler);
    }

    otherWindowLocalEvents(projectId: string, projectEvents: ProjectLocalEvent[]): void {
        log.debug("ProjectBus event triggered: otherWindowLocalEvents");
        this.eventBus.publish('OTHER_WIND0W_LOCAL_EVENTS', { projectId, projectEvents });
    }

    subscribe_otherWindowLocalEvents(handler: (data: ProjectBusEvents['OTHER_WIND0W_LOCAL_EVENTS']) => Promise<void>): { unsubscribe: () => void } {
        return this.eventBus.subscribe('OTHER_WIND0W_LOCAL_EVENTS', handler);
    }

    tabFocusChanged(isFocused: boolean): void {
        log.debug(`ProjectBus event triggered: tabFocusChanged to ${isFocused ? 'focused' : 'blurred'}`);
        this.eventBus.publish('TAB_FOCUS_CHANGED', { isFocused });
    }

    subscribe_tabFocusChanged(handler: (data: ProjectBusEvents['TAB_FOCUS_CHANGED']) => Promise<void>): { unsubscribe: () => void } {
        return this.eventBus.subscribe('TAB_FOCUS_CHANGED', handler);
    }

    serverIssue(serverIssue: ServerIssueType): void {
        log.debug("ProjectBus event triggered: serverIssue");
        this.eventBus.publish('SERVER_ISSUE', { serverIssue });
    }

    subscribe_serverIssue(handler: (data: ProjectBusEvents["SERVER_ISSUE"]) => Promise<void>): { unsubscribe: () => void; } {
        return this.eventBus.subscribe('SERVER_ISSUE', handler);
    }

    projectLoaded(projectId: string): void {
        log.debug("ProjectBus event triggered: projectLoaded");
        this.eventBus.publish('PROJECT_LOADED', { projectId });
    }
    subscribe_projectLoaded(onProjectLoaded: (data: ProjectBusEvents["PROJECT_LOADED"]) => Promise<void>): { unsubscribe: () => void; } {
        return this.eventBus.subscribe('PROJECT_LOADED', onProjectLoaded);
    }
    dialogStateChanged(isOpen: boolean, dialogTitle: string, dialogDescription?: string): void {
        log.debug("ProjectBus event triggered: dialogStateChanged");
        this.eventBus.publish('DIALOG_STATE_CHANGED', { isOpen, dialogTitle, dialogDescription });
    }
    subscribe_dialogStateChanged(handler: (data: ProjectBusEvents["DIALOG_STATE_CHANGED"]) => Promise<void>): { unsubscribe: () => void; } {
        return this.eventBus.subscribe('DIALOG_STATE_CHANGED', handler);
    }
    projectMetadataUpdated(projectMetadata: ProjectMetadata): void {
        log.debug("ProjectBus event triggered: projectMetadataUpdated");
        this.eventBus.publish('PROJECT_METADATA_UPDATED', { projectMetadata });
    }
    subscribe_projectMetadataUpdated(handler: (data: ProjectBusEvents["PROJECT_METADATA_UPDATED"]) => Promise<void>): { unsubscribe: () => void; } {
        return this.eventBus.subscribe('PROJECT_METADATA_UPDATED', handler);
    }
    keyDown(key: string, code: string, altKey: boolean, ctrlKey: boolean, metaKey: boolean, shiftKey: boolean, preventDefault: () => void, stopPropagation: () => void): void {
        log.debug("ProjectBus event triggered: keyDown");
        this.eventBus.publish('KEY_DOWN', { key, code, altKey, ctrlKey, metaKey, shiftKey, preventDefault, stopPropagation });
    }
    subscribe_keyDown(handler: (data: ProjectBusEvents["KEY_DOWN"]) => Promise<void>): { unsubscribe: () => void; } {
        return this.eventBus.subscribe('KEY_DOWN', handler);
    }

    userHitBackButton(): void {
        log.debug("ProjectBus event triggered: userHitBackButton");
        this.eventBus.publish('NAVIGATION_CHANGED', { isBackButton: true });
    }
    subscribe_userHitBackButton(handler: (data: ProjectBusEvents["NAVIGATION_CHANGED"]) => Promise<void>): { unsubscribe: () => void; } {
        return this.eventBus.subscribe('NAVIGATION_CHANGED', handler);
    }

    projectionUpdated = (projectId: string, mutationResult: ProjectProjectionResult): void => {
        log.debug("ProjectBus event triggered: projectionUpdated");
        this.eventBus.publish('PROJECTION_UPDATED', { projectId, mutationResult });
    }
    subscribe_projectionUpdated = (handler: (data: ProjectBusEvents["PROJECTION_UPDATED"]) => Promise<void>): { unsubscribe: () => void; } => {
        return this.eventBus.subscribe('PROJECTION_UPDATED', handler);
    }

    eventRaised = (projectEvent: ProjectLocalEvent): void => {
        log.debug("ProjectBus event triggered: eventRaised");
        this.eventBus.publish('EVENT_RAISED', { projectEvent });
    }
    subscribe_eventRaised = (handler: (data: ProjectBusEvents["EVENT_RAISED"]) => Promise<void>): { unsubscribe: () => void; } => {
        return this.eventBus.subscribe('EVENT_RAISED', handler);
    }

    themeChanged = (theme: ThemeMode, isDarkMode: boolean): void => {
        log.debug("ProjectBus event triggered: themeChanged");
        this.eventBus.publish('THEME_CHANGED', { theme, isDarkMode });
    }
    subscribe_themeChanged = (handler: (data: ProjectBusEvents["THEME_CHANGED"]) => Promise<void>): { unsubscribe: () => void; } => {
        return this.eventBus.subscribe('THEME_CHANGED', handler);
    }

    connectivityChanged = (isOnline: boolean): void => {
        log.debug(`ProjectBus event triggered: connectivityChanged to ${isOnline ? 'online' : 'offline'}`);
        this.eventBus.publish('CONNECTIVITY_CHANGED', { isOnline });
    }
    subscribe_connectivityChanged = (handler: (data: ProjectBusEvents["CONNECTIVITY_CHANGED"]) => Promise<void>): { unsubscribe: () => void; } => {
        return this.eventBus.subscribe('CONNECTIVITY_CHANGED', handler);
    }

}